// Die Interaktionstaste im Client (task-013, ADR 0027). Seit der Server über Besitz, Tiere und
// Tor entscheidet, führt der Client keine Handlung mehr selbst aus: Die Taste der Handlung im
// Blick wird eine `interact`-Nachricht, und die Antwort des Servers (`actionResult`) wird – wenn
// sie eine Ablehnung ist – ein kurzer Text im Bild. Was sich dadurch ändert (Milch, Tor, Kuh),
// kommt über `inventory` und `farm` zurück, nicht über diese Antwort.
//
// Reine Logik ohne DOM; main.ts reicht Zeit, Handlung und Nachrichten herein.
import {
  PROTOCOL_VERSION,
  availableActions,
  findTarget,
  type Action,
  type ActionResultMessage,
  type GameState,
  type InteractMessage,
  type InteractionContext,
} from '@bauernhof/shared';

/**
 * So lange gilt eine Anfrage als unterwegs. Kommt die Antwort nicht (Verbindung weg), darf man es
 * danach wieder versuchen; kommt sie, sofort.
 */
export const ANTWORT_WARTEZEIT_MS = 1500;

/** Der Text für eine Ablehnung, die keinen eigenen Satz mitbringt. */
export const STANDARD_ABLEHNUNG = 'Das geht gerade nicht.';

/**
 * Die erste Handlung am Ziel im Blick, mit Besitz: Auf einer fremden Parzelle ist die Liste leer,
 * also gibt es keinen Hinweis, und die Taste bewirkt nichts.
 */
export function handlungImBlick(state: GameState, kontext: InteractionContext): Action | undefined {
  return availableActions(state, findTarget(state, kontext), kontext)[0];
}

export interface Interaktion {
  /**
   * Die Taste der Handlung wurde gedrückt. Liefert die Nachricht an den Server, oder null, wenn
   * nichts im Blick ist oder die Antwort auf die vorige noch aussteht – ein Tor soll nicht zweimal
   * umschalten, weil zwei Anfragen unterwegs waren.
   */
  loese(handlung: Action | undefined, jetzt: number): InteractMessage | null;
  /** Die Antwort des Servers. Liefert den Text, der kurz eingeblendet wird, oder null bei Erfolg. */
  antwort(msg: ActionResultMessage): string | null;
  /** Ob noch eine Antwort aussteht. */
  wartet(jetzt: number): boolean;
  /** Vergisst eine ausstehende Anfrage – nach einer neuen Verbindung kommt ihre Antwort nicht mehr. */
  vergiss(): void;
}

export function createInteraktion(): Interaktion {
  let gesendetUm: number | null = null;
  const wartet = (jetzt: number): boolean => gesendetUm !== null && jetzt - gesendetUm < ANTWORT_WARTEZEIT_MS;

  return {
    loese(handlung, jetzt) {
      if (handlung === undefined || wartet(jetzt)) return null;
      gesendetUm = jetzt;
      return { v: PROTOCOL_VERSION, t: 'interact', target: { ...handlung.target } };
    },
    antwort(msg) {
      gesendetUm = null;
      if (msg.ok) return null;
      return msg.message !== undefined && msg.message !== '' ? msg.message : STANDARD_ABLEHNUNG;
    },
    wartet,
    vergiss() {
      gesendetUm = null;
    },
  };
}
